import { useSubscriptionAccess } from "@/hooks/useSubscriptionAccess";
import {
  Button,
  GradeIcon as Grade,
  PersonOutlineIcon as PersonOutline,
  RemoveIcon as Remove,
  Tooltip,
  Typography,
} from "@iluvatar/ui-kit";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import dayjs, { extend } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useState } from "react";
extend(relativeTime);

import { IFileReference } from "@iluvatar/global/src/typings";

import { ErrorBoBoMessage } from "@/components/ErrorBoBoMessage";
import { MessageContainer } from "@/components/MessageContainer";
import { SubscribedFilters } from "@/components/SubscribedFilters";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useToastContext } from "@/contexts/toast.context";
import { useQueryLikes } from "@/hooks/getMatchQuery";
import { useNotificationDismissal } from "@/hooks/notificationDismissal";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useStorageUrl } from "@/hooks/useStorageUrl";
import { useSubscribedFilters } from "@/hooks/useSubscribedFilters";
import DefaultLayout from "@/layout/default-page";
import { imgRefSize } from "@/services/file";
import { cn } from "@/utilities";

const LikesPage: React.FC = () => {
  useNotificationDismissal("likes");

  const { user, api } = useCurrentUser();
  const hasSubscriptionAccess = useSubscriptionAccess(user?.isSubscribed);
  const { addToast } = useToastContext();
  const [filtered, setFiltered] = useState<string[]>([]);
  const communityId = "default";
  const { filters, settings, setSettings } = useSubscribedFilters(
    "likes",
    user?.uid,
  );
  const [likes, errors, loadMore] = useQueryLikes({
    communityId,
    filters,
    isSubscribed: hasSubscriptionAccess,
  });

  const action = useMutation({
    mutationFn: async ({
      userId,
      kind,
    }: {
      userId: string;
      kind: "like" | "ignore";
    }) => {
      await (kind === "like"
        ? api.likeUser(userId, communityId)
        : api.ignoreUser(userId, communityId));
      return userId;
    },
    onSuccess: (userId) => setFiltered((current) => [...current, userId]),
  });
  const handleAction = (userId: string, kind: "like" | "ignore") => {
    if (!hasSubscriptionAccess) {
      addToast(
        kind === "like"
          ? "You can subscribe to like back."
          : "You can subscribe to ignore incoming likes.",
        { variant: "info" },
      );
      return;
    }
    if (!action.isPending) {
      action.mutate({ userId, kind });
    }
  };

  const _total = user?.likesCountMap[communityId] || 0;

  return (
    <DefaultLayout
      className={
        "profile-container ml-auto mr-auto [max-width:900px] flex flex-col [background:linear-gradient(180deg,_transparent_0%,_transparent_50%,_var(--color-background-default)_50%,_var(--color-background-default))] [@media(min-width:_900px)]:[--page-content-padding:42px] [@media(min-width:_900px)]:[padding-left:42px] [@media(min-width:_900px)]:[padding-right:42px] [&.profile-container--input]:[max-width:900px] [&.profile-container--input]:w-full [@media(max-width:_400px)]:[&.main-container]:[--page-content-padding:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-left:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-right:8px] flex start-start pb-12"
      }
      style={{
        height:
          user && _total == 0 ? "calc(100vh - var(--nav-height) - 1px)" : "",
      }}
    >
      <div className="flex w-full items-center justify-between gap-4 mt-8 mb-4">
        <Typography variant="h2">
          {user && _total > 0 && (
            <span className="text-[var(--color-primary)] mr-2">{_total}</span>
          )}
          Like{_total !== 1 ? "s" : ""}
        </Typography>
        <div className="ml-auto">
          <SubscribedFilters
            isSubscribed={hasSubscriptionAccess}
            key={user?.uid}
            settings={settings}
            onChange={setSettings}
          />
        </div>
      </div>

      <ErrorBoBoMessage error={errors || action.error} />

      {user && !errors && _total === 0 && (
        <MessageContainer>
          <Typography variant="h2">No Likes yet</Typography>
          <Typography variant="caption" className="[margin-top:8px]">
            Keep at it!
          </Typography>
        </MessageContainer>
      )}

      {(likes || [])
        ?.filter((m) => !m?.uid || !filtered.includes(m?.uid))
        .map((userProfile) => {
          return (
            <LikedUserPreview
              communityId={communityId}
              disabled={action.isPending}
              isSubscribed={hasSubscriptionAccess}
              isMatched={Boolean(
                user?.matchMap?.[communityId]?.includes(userProfile.uid),
              )}
              key={userProfile.uid}
              uid={userProfile.uid}
              gender={userProfile.gender}
              subTitle={userProfile.pronouns}
              sexuality={userProfile.sexuality}
              displayName={userProfile.displayName!}
              imagesById={userProfile.profile.imagesById || {}}
              imageIds={userProfile.profile.imageIds || []}
              onAction={handleAction}
            />
          );
        })}

      {loadMore && (
        <Button variant="outline" onClick={loadMore} className="mt-5">
          Load More
        </Button>
      )}
    </DefaultLayout>
  );
};

export const Route = createFileRoute("/likes")({
  component: LikesPage,
});

function LikedUserPreview({
  communityId,
  uid,
  displayName,
  imagesById,
  imageIds,
  className,
  birthDate,
  gender,
  sexuality,
  subTitle,
  isMatched,
  isSubscribed,
  disabled,
  onAction,
  ...rest
}: {
  uid?: string;
  communityId: string;
  displayName: string;
  birthDate?: number;
  gender?: string;
  sexuality?: string;
  subTitle?: string;
  imagesById: Record<string, IFileReference>;
  imageIds: string[];
  isMatched?: boolean;
  isSubscribed?: boolean;
  disabled?: boolean;
  onAction: (uid: string, kind: "like" | "ignore") => void;
} & React.HTMLAttributes<HTMLDivElement>) {
  if (!displayName) {
    return <div>err</div>;
  }
  return (
    <div
      {...rest}
      className={cn(
        className,
        "flex items-center gap-3 sm:gap-4 w-full min-h-[96px] sm:min-h-[112px] p-3 sm:p-4 mt-3 border border-[var(--color-primary--active)] rounded-[34px]",
        isSubscribed && "hover:border-[var(--color-primary)]",
      )}
    >
      <div className="flex gap-2 shrink-0 max-w-[32%] overflow-x-auto overflow-y-hidden">
        {imageIds.length ? (
          imageIds.map((iid) => (
            <ImagePrev
              communityId={communityId}
              key={iid}
              imgRef={imagesById[iid]}
              displayName={displayName}
              blur={
                !isMatched &&
                (!isSubscribed || Boolean(imagesById[iid]?.metadata.isPrivate))
              }
            />
          ))
        ) : (
          <div className="flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-[22px] bg-[var(--color-primary--hover)] text-[var(--color-primary)]">
            <PersonOutline className="[font-size:large]" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 break-words">
        <Typography variant="h4" className="mb-1">
          {displayName}
        </Typography>
        {(birthDate || gender || sexuality) && (
          <Typography variant="subtitle2">
            {birthDate ? `${dayjs(new Date()).diff(birthDate, "years")}, ` : ""}
            {[gender, sexuality].filter(Boolean).join(" ")}
          </Typography>
        )}
        {subTitle && <Typography variant="subtitle2">{subTitle}</Typography>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Tooltip
          content={
            isSubscribed ? "Ignore" : "Subscribe to ignore incoming likes"
          }
        >
          <span className="inline-flex">
            <Button
              variant="outline"
              aria-label={`Ignore ${displayName}`}
              disabled={!uid || disabled}
              aria-disabled={!isSubscribed || !uid || Boolean(disabled)}
              onClick={uid ? () => onAction(uid, "ignore") : undefined}
              className={[
                "bg-[var(--color-primary--hover)] p-[9px] min-w-0",
                "[color:rgb(var(--uik-info))] [border-radius:12px]",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{ opacity: !isSubscribed ? 0.38 : 1 }}
            >
              <Remove />
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          content={isSubscribed ? "Like back" : "Subscribe to like back"}
        >
          <span className="inline-flex">
            <Button
              variant="outline"
              disabled={!uid || disabled}
              aria-disabled={!isSubscribed || !uid || Boolean(disabled)}
              aria-label={`Like ${displayName}`}
              onClick={uid ? () => onAction(uid, "like") : undefined}
              className={[
                "bg-[var(--color-primary--hover)] p-[9px] min-w-0",
                "[border-radius:12px]",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{ opacity: !isSubscribed ? 0.38 : 1 }}
            >
              <Grade />
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
}

function ImagePrev({
  communityId,
  imgRef,
  blur,
  displayName,
}: {
  imgRef?: IFileReference;
  communityId: string;
  blur?: boolean;
  displayName: string;
}) {
  const [fileUrl] = useStorageUrl(
    imgRefSize(imgRef, 300, blur),
    Boolean(imgRef?.metadata.isPrivate && !blur),
    communityId,
  );
  return (
    <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0">
      <AspectRatio ratio={1} className="overflow-hidden rounded-[22px]">
        <img
          src={fileUrl}
          draggable={false}
          alt={`${displayName}'s photo`}
          className="w-full h-full object-cover"
        />
      </AspectRatio>
    </div>
  );
}
